import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { PROJECTS, PROJECT_CATS } from "@/data/site";
import { FadeUp, Chapter, RevealLines, EASE } from "./motion";

export default function Portfolio() {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);

  const filtered = cat === "All" ? PROJECTS : PROJECTS.filter((p) => p.cat === cat);
  const idx = active ? filtered.findIndex((p) => p.id === active.id) : -1;
  const nav = (dir) => setActive(filtered[(idx + dir + filtered.length) % filtered.length]);

  return (
    <section id="projects" data-testid="portfolio-section" className="bg-[#F7F5F2] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter num="05" title="Selected Works" />
        <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
          <RevealLines
            className="font-display text-4xl tracking-tight text-[#1B1B1B] md:text-6xl"
            lines={[<>Homes we're</>, <><span className="italic text-[#B68D40]">proud</span> of.</>]}
          />
        </div>

        <FadeUp delay={0.2} className="mt-12 flex flex-wrap gap-2">
          {PROJECT_CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                cat === c ? "bg-[#1B1B1B] text-[#F7F5F2]" : "border hairline text-[#2C2C2C]/60 hover:border-[#B68D40] hover:text-[#B68D40]"
              }`}
            >
              {c}
            </button>
          ))}
        </FadeUp>

        <motion.div layout className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.div
                layout
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="mb-5 break-inside-avoid"
              >
                <button
                  onClick={() => setActive(p)}
                  data-testid={`project-${p.id}`}
                  data-cursor="View"
                  className="group relative block w-full overflow-hidden rounded-xl text-left"
                >
                  <img
                    src={p.img}
                    alt={`${p.title} — ${p.cat} interior design ${p.loc}, Bangalore`}
                    loading="lazy"
                    style={{ height: p.h }}
                    className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B1B]/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="absolute inset-x-0 bottom-0 translate-y-4 p-6 opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">{p.cat} · {p.loc}</p>
                    <h3 className="mt-1 font-display text-xl text-[#F7F5F2]">{p.title}</h3>
                  </div>
                  <span className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F5F2]/15 text-[#F7F5F2] opacity-0 backdrop-blur-md transition-opacity duration-500 group-hover:opacity-100">
                    <Expand size={15} />
                  </span>
                  {p.real && (
                    <span className="absolute left-5 top-5 rounded-full bg-[#B68D40] px-3 py-1 font-num text-[9px] uppercase tracking-[0.25em] text-[#1B1B1B]">
                      Delivered
                    </span>
                  )}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1B1B1B]/95 p-4 backdrop-blur-sm md:p-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <button data-testid="lightbox-close" onClick={() => setActive(null)} className="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F2]/25 text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B]" aria-label="Close">
              <X size={20} />
            </button>
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); nav(-1); }} className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#F7F5F2]/25 text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B] md:left-8" aria-label="Previous">
              <ChevronLeft size={20} />
            </button>
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); nav(1); }} className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#F7F5F2]/25 text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B] md:right-8" aria-label="Next">
              <ChevronRight size={20} />
            </button>
            <motion.figure
              key={active.id}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="max-h-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.img.replace("w=1000", "w=1600")} alt={active.title} className="max-h-[74vh] w-full rounded-lg object-cover" />
              <figcaption className="mt-5 flex items-end justify-between">
                <div>
                  <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">{active.cat} · {active.loc}</p>
                  <h3 className="mt-1 font-display text-2xl text-[#F7F5F2]">{active.title}</h3>
                  {active.slug && (
                    <a
                      href={`/project/${active.slug}`}
                      data-testid="lightbox-case-study-link"
                      className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#B68D40]/60 px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-[#B68D40] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B]"
                    >
                      View Full Case Study
                    </a>
                  )}
                </div>
                <span className="font-num text-xs text-[#F7F5F2]/40">{idx + 1} / {filtered.length}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
